import { Grid, Paper, Typography } from '@material-ui/core';
import { cloneDeep } from 'lodash';
import moment from 'moment-timezone';
import React, { ReactElement } from 'react';
import { Bar, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { IRealtimeAnalytics, IUser } from '~/src/dash/api/console-api';
import { IAnalytics } from '~/src/dash/api/console-api';
import {
  dashboardKeyAVOCGroupMRate,
  dashboardKeyAVOCRate,
  dashboardKeyBlockedRate,
  dashboardKeyInvalidTrafficRate,
  dashboardKeyMeasuredRate,
  dashboardKeyRenderRate,
  dashboardKeyViewableRate,
  dashboardTitleAVOCGroupMRate,
  dashboardTitleAVOCRate,
  dashboardTitleBlockedAds,
  dashboardTitleBlockedRate,
  dashboardTitleEligibleAds,
  dashboardTitleImpressionsRendered,
  dashboardTitleInvalidTrafficAds,
  dashboardTitleInvalidTrafficRate,
  dashboardTitleMeasuredAds,
  dashboardTitleMeasuredRate,
  dashboardTitleRenderRate,
  dashboardTitleTrackedAds,
  dashboardTitleViewableImpressions,
  dashboardTitleViewableRate,
} from '~/src/dash/api/ias-wrapper-api/constants';
import { DashboardDataToggles } from '~/src/dash/components/dashboard/dashboard-data-toggles';
import { DashboardDateToggles } from '~/src/dash/components/dashboard/dashboard-date-toggles';
import { IGraphDataTypes } from '~/src/dash/components/dashboard/dashboard-graph';
import { DashboardTooltipV2 } from '~/src/dash/components/dashboard/dashboard-tooltip-v2';
import { DashedBar } from '~/src/dash/components/dashboard/dashed-bar';
import {
  DashboardCardDataKeys,
  DashboardCardDataTitles,
  IMeasurementAggregateCardDataDisplay,
  ISelectedMetric,
} from '~/src/dash/globals/constants';
import { ISelectedMetrics } from '~/src/dash/types/metrics';
import {
  DateGroup,
  DateGroupDays,
  DateGroupHours,
  DateGroupMonths,
  DateGroupWeeks,
  formatDayMonthName,
  formatHourDayMonth,
} from '~/src/dash/utils/date';
import { getTransparentColor } from '~/src/dash/utils/ui';
import { IFirstAndThirdPartyDashboardCardDataDisplay } from '~/src/dash/views/measurement-home-view';
import {
  IStreamAggregateCardDataDisplay,
  IStreamErrorCardDataDisplay,
  IStreamKPICardDataDisplay,
  IStreamLatencyCardDataDisplay,
  networkHomeAggDataKeys,
  networkHomeAggDataTitles,
  networkHomeLiveDataTitles,
  StreamDashboardCardDataKeys,
} from '~/src/dash/views/networks/constants';

const {
  AGG_STREAM_ADS_FILLED_TITLE,
  AGG_STREAM_POD_REQUESTS_TITLE,
  AGG_STREAM_STREAM_ERROR_TITLE,
  AGG_STREAM_PODS_FILLED_TITLE,
  AGG_STREAM_AD_IMPRESSIONS_TITLE,
  AGG_STREAM_AVG_STREAM_AD_FILL_LATENCY_TITLE,
  AGG_STREAM_ADS_FETCH_TIMEOUT_TITLE,
  AGG_STREAM_CHUNKLIST_FETCH_TITLE,
  AGG_STREAM_CHUNKLIST_RESPONSE_TITLE,
  AGG_STREAM_START_TITLE,
  AGG_STREAM_PLAYLIST_TIMEOUT_TITLE,
} = networkHomeAggDataTitles;

const { AGG_AVG_STREAM_AD_FILLED_LATENCY_KEY } = networkHomeAggDataKeys;

const {
  STREAM_AD_FILLED_TITLE,
  STREAM_POD_REQUEST_TITLE,
  STREAM_POD_FILLED_TITLE,
  STREAM_AD_IMPRESSION_TITLE,
  STREAM_AD_COMPLETE_TITLE,
  STREAM_CHUNKLIST_FETCH_TITLE,
  STREAM_CHUNKLIST_RESPONSE_TITLE,
  STREAM_PLAYLIST_TIMEOUT_TITLE,
  STREAM_START_TITLE,
  STREAM_AD_FILLED_LATENCY_TITLE,
  STREAM_AD_FETCH_TIMEOUT_TITLE,
  STREAM_CHUNKLIST_TIMEOUT_TITLE,
  STREAM_TRANSCODE_MISS_ERROR_TITLE,
  STREAM_CACHED_AD_MISS_TITLE,
  STREAM_ADS_FETCH_ERROR_TITLE,
  STREAM_PLAYLIST_ERROR_TITLE,
  STREAM_CHUNKLIST_ERROR_TITLE,
  STREAM_CACHE_READ_MISS_ERROR_TITLE,
} = networkHomeLiveDataTitles;

interface IProps {
  classes: { [key: string]: string };
  metricsInfo:
    | IStreamAggregateCardDataDisplay
    | IStreamKPICardDataDisplay
    | IStreamLatencyCardDataDisplay
    | IStreamErrorCardDataDisplay
    | IFirstAndThirdPartyDashboardCardDataDisplay;
  handleDensityRangeChange: (densityRange: string | number) => void;
  loaded: boolean;
  // loading indicators / debounce
  loadingState1: LoadingState;
  loadingState2: LoadingState;
  graphTitle: string;
  toggleLiveData: boolean;
  mergeLiveData?: boolean;
  compareEnabled?: boolean;
  densityRange?: DateGroup;
  densityRangeLive?: number;
  calStartDate?: moment.Moment;
  calEndDate?: moment.Moment;
  compStartDate?: moment.Moment;
  compEndDate?: moment.Moment;
  data1: IAnalytics[];
  data2: IAnalytics[];
  dataLive: IRealtimeAnalytics[];
  currencySymbol: string;
  handleToggleMergeLiveData?: () => void;
  handleMetricsChange: (title: StreamDashboardCardDataKeys, color: string) => void;
  certifiedViewable?: boolean;
  hourAggRangeDisabled?: boolean;
  selectedMetricsColorMap: Record<string, string>;
  selectedMetrics: ISelectedMetric[];
  selectedMetricsDataToggle: ISelectedMetrics;
  selectedMetricsGraphData: IGraphDataTypes;
  user: IUser;
}

export interface IDataItem {
  Date: moment.Moment | string;
  CDate: moment.Moment | string; // CDate, CompareRevenue, etc represent the compare data
  DateLabel: string;
  AdsFilled: number;
  PodRequests: number;
  StreamError: number;
  PodsFilled: number;
  AdImpressions: number;
  AvgStreamAdFilledLatency: number;
  AdsFetchTimeout: number;
  // Stream KPI Metrics
  StreamAdFilled: number;
  StreamPodRequest: number;
  StreamPodFilled: number;
  StreamAdImpression: number;
  StreamChunklistFetch: number;
  StreamChunklistResponse: number;
  StreamAdComplete: number;
  StreamStart: number;
  // Stream Latency Metrics
  StreamAdFilledLatency: number;
  StreamFetchTimeout: number;
  StreamPlaylistTimeout: number;
  StreamChunklistTimeout: number;
  // Stream Error Metrics
  StreamTranscodeMiss: number;
  StreamCachedAdMiss: number;
  StreamAdsFetchError: number;
  StreamPlaylistError: number;
  StreamChunklistError: number;
  StreamCacheReadMissError: number;
  CompareAdsFilled: number;
  ComparePodRequests: number;
  CompareStreamError: number;
  ComparePodsFilled: number;
  CompareAdImpressions: number;
  CompareAvgStreamAdFilledLatency: number;
  CompareAdsFetchTimeout: number;
  CompareStreamChunklistFetch: number;
  CompareStreamChunklistResponse: number;
  CompareStreamStart: number;
  CompareStreamPlaylistTimeout: number;
  Time: string;
  [key: string]: any;
}

export type LoadingState = number;
export const LoadingStateNoData = 2;

export const MakeDot = (color: string, opacity = 1) => {
  return (
    <Grid
      item
      style={{
        backgroundColor: `${color}`,
        borderRadius: 99,
        height: 10,
        width: 10,
        opacity,
      }}
    />
  );
};

export const checkKeysForString = (keys: string[], targets: string[]): boolean => {
  for (const target of targets) {
    if (keys.includes(target)) {
      return true;
    }
  }
  return false;
};

// aggregate graph => creates a date label incrementing by the densityRange
const assignDateLabel = (
  currentDataItem: IDataItem,
  i: number,
  densityRange: DateGroup,
): IDataItem => {
  if (densityRange === DateGroupHours) {
    currentDataItem.DateLabel = 'Hour ' + (i + 1).toString();
  }
  if (densityRange === DateGroupDays) {
    currentDataItem.DateLabel = 'Day ' + (i + 1).toString();
  }
  if (densityRange === DateGroupWeeks) {
    currentDataItem.DateLabel = 'Week ' + (i + 1).toString();
  }
  if (densityRange === DateGroupMonths) {
    currentDataItem.DateLabel = 'Month ' + (i + 1).toString();
  }
  return currentDataItem;
};

const blankDataItem: IDataItem = {
  Date: moment(),
  DateLabel: 'Day 1',
  CDate: moment(),
  AdsFilled: 0,
  PodRequests: 0,
  StreamError: 0,
  PodsFilled: 0,
  AdImpressions: 0,
  AvgStreamAdFilledLatency: 0,
  AdsFetchTimeout: 0,
  // Stream KPI Metrics
  StreamAdFilled: 0,
  StreamPodRequest: 0,
  StreamPodFilled: 0,
  StreamAdImpression: 0,
  StreamChunklistFetch: 0,
  StreamChunklistResponse: 0,
  StreamAdComplete: 0,
  StreamStart: 0,
  // Stream Latency
  StreamAdFilledLatency: 0,
  StreamFetchTimeout: 0,
  StreamPlaylistTimeout: 0,
  StreamChunklistTimeout: 0,
  // Stream Error
  StreamTranscodeMiss: 0,
  StreamCachedAdMiss: 0,
  StreamAdsFetchError: 0,
  StreamPlaylistError: 0,
  StreamChunklistError: 0,
  StreamCacheReadMissError: 0,
  CompareAdsFilled: 0,
  ComparePodRequests: 0,
  CompareStreamError: 0,
  ComparePodsFilled: 0,
  CompareAdImpressions: 0,
  CompareAvgStreamAdFilledLatency: 0,
  CompareAdsFetchTimeout: 0,
  CompareStreamChunklistFetch: 0,
  CompareStreamChunklistResponse: 0,
  CompareStreamStart: 0,
  CompareStreamPlaylistTimeout: 0,
  Time: '',
};

// merges the data from data1 and data2 (agg compare data) into one item so it can be displayed on the graph
const mergeData = (
  data1: IAnalytics[],
  data2: IAnalytics[],
  calStartDate: moment.Moment,
  compStartDate: moment.Moment,
  calEndDate: moment.Moment,
  compEndDate: moment.Moment,
  densityRange: DateGroup,
  selectedMetrics: IStreamAggregateCardDataDisplay | IMeasurementAggregateCardDataDisplay,
  compareEnabled: boolean,
): IDataItem[] => {
  if (data1.length === 0) {
    return [];
  }
  const data = [];

  let offset = 0;
  let offsetComp = 0;
  if (densityRange === DateGroupHours) {
    offset = moment(calEndDate).utcOffset() - moment(calStartDate).utcOffset();
    offsetComp = moment(compEndDate).utcOffset() - moment(compStartDate).utcOffset();
  }

  // only handle the special case when summer -> winter when sunday has two 1am-2am
  let currentCalDate: moment.Moment =
    offset === -60 ? moment(calStartDate).add(offset, 'minute') : moment(calStartDate);
  let currentCompDate: moment.Moment =
    offsetComp === -60 ? moment(compStartDate).add(offset, 'minute') : moment(compStartDate);

  // calTotal, compTotal represents the difference between the start and end dates in the value of the densityRange (days, hours, weeks, months)
  let calTotal = 0,
    compTotal = 0;
  let data1Index: number = 0,
    data2Index: number = 0;
  if (densityRange === DateGroupDays) {
    calTotal = moment(calEndDate).diff(currentCalDate, 'days') + 1;
    compTotal = moment(compEndDate).diff(currentCompDate, 'days') + 1;
  } else if (densityRange === DateGroupHours) {
    calTotal = moment(calEndDate).diff(currentCalDate, 'hours') + 1;
    compTotal = moment(compEndDate).diff(currentCompDate, 'hours') + 1;
  } else if (densityRange === DateGroupWeeks) {
    calTotal =
      moment(calEndDate).endOf('week').diff(moment(currentCalDate).startOf('week'), 'weeks') + 1;
    compTotal =
      moment(compEndDate).endOf('week').diff(moment(currentCompDate).startOf('week'), 'weeks') + 1;
  } else if (densityRange === DateGroupMonths) {
    calTotal =
      moment(calEndDate).endOf('month').diff(moment(currentCalDate).startOf('month'), 'months') + 1;
    compTotal =
      moment(compEndDate).endOf('month').diff(moment(currentCompDate).startOf('month'), 'months') +
      1;
  }
  const numItems = compareEnabled ? Math.max(calTotal, compTotal) : calTotal;
  for (let i = 0; i < numItems; i++) {
    // create a data item from blankDataItem and assign the date
    const currentDataItem = Object.assign({}, blankDataItem);
    assignDateLabel(currentDataItem, i, densityRange);
    // if the data1 has the index, the index is in range of the difference in date and the dates match
    // fill out the data for the new data item

    if (
      data1[data1Index] &&
      data1Index < calTotal &&
      dateMatches(densityRange, moment(data1[data1Index].Date).tz('UTC'), currentCalDate)
    ) {
      for (const value of Object.values(selectedMetrics)) {
        const key = value.key;
        currentDataItem[key] = data1[data1Index][key];
      }
      data1Index++;
    }

    // if the data2 (compare) has the index, the index is in range of the difference in date and the dates match
    // fill out the data for the new data item
    if (
      data2[data2Index] &&
      compareEnabled &&
      data2Index < compTotal &&
      dateMatches(densityRange, moment(data2[data2Index].Date).tz('UTC'), currentCompDate)
    ) {
      for (const value of Object.values(selectedMetrics)) {
        const compareKey = value.compareKey;
        const key = value.key;
        currentDataItem[compareKey] = data2[data2Index][key];
      }
      data2Index++;
    }

    // assign date values to the data item and increment the current dates
    if (densityRange === DateGroupHours) {
      currentDataItem.CDate = formatHourDayMonth(currentCompDate);
      currentDataItem.Date = formatHourDayMonth(currentCalDate);
      currentCalDate = currentCalDate.add(1, 'hours');
      currentCompDate = currentCompDate.add(1, 'hours');
    } else {
      currentDataItem.CDate = formatDayMonthName(currentCompDate);
      currentDataItem.Date = formatDayMonthName(currentCalDate);
      if (densityRange === DateGroupDays) {
        currentCalDate = currentCalDate.add(1, 'days');
        currentCompDate = currentCompDate.add(1, 'days');
      } else if (densityRange === DateGroupWeeks) {
        currentCalDate = currentCalDate.add(1, 'weeks');
        currentCompDate = currentCompDate.add(1, 'weeks');
      } else if (densityRange === DateGroupMonths) {
        currentCalDate = currentCalDate.add(1, 'months');
        currentCompDate = currentCompDate.add(1, 'months');
      }
    }
    // in dst change day, there is an unneeded empty data item inserted, which needs to be removed
    if (offset === -60) {
      if (data[data.length - 1] && currentDataItem.Date === data[data.length - 1].Date) {
        continue;
      }
    }
    data.push(currentDataItem);
  }
  return data;
};

// for aggregate density range values, checks if dates are the same
const dateMatches = (densityRange: string, date1: moment.Moment, date2: moment.Moment): boolean => {
  if (densityRange === DateGroupHours) {
    return date1.format('HH:mm MMM D') === date2.format('HH:mm MMM D');
  } else if (densityRange === DateGroupDays) {
    return date1.format('MMM D') === date2.format('MMM D');
  } else if (densityRange === DateGroupWeeks) {
    return date1.week() === date2.week();
  } else if (densityRange === DateGroupMonths) {
    return date1.month() === date2.month();
  }
  return false;
};

export const DashboardGraphV2 = (props: IProps) => {
  const {
    classes,
    densityRange,
    handleDensityRangeChange,
    calStartDate,
    calEndDate,
    loaded,
    data1,
    data2,
    graphTitle,
    dataLive,
    compareEnabled,
    compStartDate,
    compEndDate,
    toggleLiveData,
    loadingState1,
    loadingState2,
    currencySymbol,
    selectedMetricsColorMap,
    selectedMetricsDataToggle,
    selectedMetricsGraphData,
    selectedMetrics,
    metricsInfo,
    user,
    handleMetricsChange,
  } = props;

  const lineDataKeys = [
    // network dashboard
    AGG_AVG_STREAM_AD_FILLED_LATENCY_KEY,
    // measurement dashboard
    dashboardKeyRenderRate,
    dashboardKeyMeasuredRate,
    dashboardKeyViewableRate,
    dashboardKeyInvalidTrafficRate,
    dashboardKeyBlockedRate,
    dashboardKeyAVOCGroupMRate,
    dashboardKeyAVOCRate,
  ] as DashboardCardDataKeys[];
  let hugeYAxisNeeded, bigYAxisNeeded, mediumYAxisNeeded, smallYAxisNeeded;
  const smallYAxisKeys = [
    // network dashboard
    // measurement dashboard
    dashboardTitleBlockedRate,
    dashboardTitleInvalidTrafficRate,
    dashboardTitleRenderRate,
    dashboardTitleMeasuredRate,
    dashboardTitleViewableRate,
    dashboardTitleAVOCGroupMRate,
    dashboardTitleAVOCRate,
  ] as string[];
  const mediumYAxisKeys = [
    // network dashboard
    AGG_STREAM_ADS_FETCH_TIMEOUT_TITLE,
    STREAM_AD_FETCH_TIMEOUT_TITLE,
    AGG_STREAM_AVG_STREAM_AD_FILL_LATENCY_TITLE,
    STREAM_AD_FILLED_LATENCY_TITLE,
    STREAM_CACHED_AD_MISS_TITLE,
  ] as string[];
  const bigYAxisKeys = [
    // network dashboard
    AGG_STREAM_PLAYLIST_TIMEOUT_TITLE,
    AGG_STREAM_STREAM_ERROR_TITLE,
    AGG_STREAM_CHUNKLIST_RESPONSE_TITLE,
    STREAM_CHUNKLIST_RESPONSE_TITLE,
    STREAM_AD_COMPLETE_TITLE,
    STREAM_PLAYLIST_TIMEOUT_TITLE,
    STREAM_CHUNKLIST_TIMEOUT_TITLE,
    STREAM_TRANSCODE_MISS_ERROR_TITLE,
    STREAM_ADS_FETCH_ERROR_TITLE,
    STREAM_PLAYLIST_ERROR_TITLE,
    STREAM_CHUNKLIST_ERROR_TITLE,
    STREAM_CACHE_READ_MISS_ERROR_TITLE,
  ] as string[];
  const hugeYAxisKeys = [
    // network dashboard
    AGG_STREAM_CHUNKLIST_FETCH_TITLE,
    STREAM_CHUNKLIST_FETCH_TITLE,
    AGG_STREAM_START_TITLE,
    STREAM_START_TITLE,
    AGG_STREAM_AD_IMPRESSIONS_TITLE,
    STREAM_AD_IMPRESSION_TITLE,
    AGG_STREAM_ADS_FILLED_TITLE,
    STREAM_AD_FILLED_TITLE,
    AGG_STREAM_POD_REQUESTS_TITLE,
    STREAM_POD_REQUEST_TITLE,
    AGG_STREAM_PODS_FILLED_TITLE,
    STREAM_POD_FILLED_TITLE,
    // measurement dashboard
    dashboardTitleTrackedAds,
    dashboardTitleImpressionsRendered,
    dashboardTitleEligibleAds,
    dashboardTitleMeasuredAds,
    dashboardTitleViewableImpressions,
    dashboardTitleInvalidTrafficAds,
    dashboardTitleBlockedAds,
  ] as string[];
  const allActiveKeys = selectedMetrics.map((metric) => metric.key);
  if (checkKeysForString(allActiveKeys, smallYAxisKeys)) {
    smallYAxisNeeded = true;
  }
  if (checkKeysForString(allActiveKeys, mediumYAxisKeys)) {
    mediumYAxisNeeded = true;
  }
  if (checkKeysForString(allActiveKeys, bigYAxisKeys)) {
    bigYAxisNeeded = true;
  }
  if (checkKeysForString(allActiveKeys, hugeYAxisKeys)) {
    hugeYAxisNeeded = true;
  }
  // priority will go to medium revenue axis, small axis is for CPM, latency
  // big axis is for impressions, endpoint requests, auction inits and winning bids
  // bidRequest and bidResponse axes are extra large and have low priority
  const hugeYAxis = (
    <YAxis
      yAxisId='huge'
      orientation='right'
      scale='auto'
      tick={{
        fontFamily: 'inherit',
      }}
      domain={[0, 'auto']}
      unit={''}
      hide={!hugeYAxisNeeded}
      tickFormatter={(value: number) => value.toLocaleString()}
    />
  );
  const bigYAxis = (
    <YAxis
      yAxisId='big'
      orientation={hugeYAxisNeeded && !smallYAxisNeeded && !mediumYAxisNeeded ? 'left' : 'right'}
      scale='auto'
      tick={{
        fontFamily: 'inherit',
      }}
      domain={[0, 'auto']}
      unit={''}
      hide={
        !bigYAxisNeeded ||
        (hugeYAxisNeeded && smallYAxisNeeded) ||
        (hugeYAxisNeeded && mediumYAxisNeeded)
      }
      tickFormatter={(value: number) => value.toLocaleString()}
    />
  );
  const mediumYAxis = (
    <YAxis
      yAxisId='medium'
      orientation={'left'}
      scale='auto'
      tick={{
        fontFamily: 'inherit',
      }}
      domain={[0, 'auto']}
      unit={''}
      hide={!mediumYAxisNeeded}
      tickFormatter={(value: number) => value.toLocaleString()}
    />
  );
  const smallYAxis = (
    <YAxis
      yAxisId='small'
      orientation={'left'}
      scale='auto'
      tick={{
        fontFamily: 'inherit',
      }}
      domain={[0, 'auto']}
      unit={''}
      hide={!smallYAxisNeeded || mediumYAxisNeeded}
      tickFormatter={(value: number) => value.toLocaleString()}
    />
  );
  // If you want to change the metrics order, change it here.
  const yAxes = [hugeYAxis, bigYAxis, smallYAxis, mediumYAxis];
  // check which graphs should be displayed and add those bars / lines to the graph as well as
  // the axes needed to display that graph
  /**
   * graphsBuilder: Builds the bars used in the graph, switch cases on live vs aggregate data.
   * @param {IStreamDashboardCardDataDisplay | IFirstAndThirdPartyDashboardCardDataDisplay} metricsInfo Specifies how to display each metric.
   * @param {ISelectedMetric[]} selectedMetrics User-selected metrics to display
   * @param {Record<string, string>} selectedMetricsColorMap Specifies the bar/line colors of chosen metrics
   * @param {DashboardCardDataKeys[]} lineDataKeys Specifies which metrics are line display, which are bar display
   * @return {ReactElement[]} An array of bars to display
   */
  const graphsBuilder = (
    metricsInfo:
      | IStreamAggregateCardDataDisplay
      | IStreamErrorCardDataDisplay
      | IStreamLatencyCardDataDisplay
      | IStreamKPICardDataDisplay
      | IFirstAndThirdPartyDashboardCardDataDisplay,
    selectedMetrics: ISelectedMetric[],
    selectedMetricsColorMap: Record<string, string>,
    lineDataKeys: DashboardCardDataKeys[],
  ): ReactElement[] => {
    // check which graphs should be displayed and add those bars / lines to the graph as well as
    // the axes needed to display that graph
    const graphs: ReactElement[] = [];
    for (let idx = 0; idx < selectedMetrics.length; idx++) {
      const currMetric = selectedMetrics[idx];
      const currMetricKey = currMetric.key as DashboardCardDataTitles;
      // @ts-ignore
      const currMetricInfo = metricsInfo[currMetricKey];
      if (currMetric.active) {
        const { yAxisID } = currMetricInfo;
        const { key, compareKey } = currMetricInfo;
        if (lineDataKeys.includes(key)) {
          graphs.push(
            <Line
              hide={!loaded}
              dot={false}
              type='monotone'
              dataKey={key}
              stroke={selectedMetricsColorMap[idx]}
              yAxisId={yAxisID}
              strokeWidth={4}
            />,
          );
          if (compareEnabled) {
            graphs.push(
              <Line
                hide={!loaded}
                dot={false}
                type='monotone'
                dataKey={compareKey}
                stroke={getTransparentColor(selectedMetricsColorMap[idx], '66')}
                yAxisId={yAxisID}
                strokeWidth={4}
                strokeDasharray='5 5'
              />,
            );
          }
        } else {
          graphs.push(
            <Bar
              hide={!loaded}
              barSize={50}
              dataKey={key}
              fill={selectedMetricsColorMap[idx]}
              yAxisId={yAxisID}
            />,
          );
          if (compareEnabled) {
            graphs.push(
              <Bar
                hide={!loaded}
                barSize={50}
                dataKey={compareKey}
                fill={getTransparentColor(selectedMetricsColorMap[idx], '66')}
                yAxisId={yAxisID}
                shape={
                  <DashedBar
                    fill={getTransparentColor(selectedMetricsColorMap[idx], '66')}
                    x={0}
                    y={0}
                    width={0}
                    height={0}
                    value={0}
                  />
                }
              />,
            );
          }
        }
      }
    }
    return graphs;
  };

  const generateGraphs = (lines: DashboardCardDataKeys[]) => {
    return graphsBuilder(
      cloneDeep(props.metricsInfo),
      selectedMetrics,
      selectedMetricsColorMap,
      lines,
    );
  };

  const data1AggMerged = mergeData(
    data1,
    data2,
    calStartDate || moment(),
    compStartDate || moment(),
    calEndDate || moment(),
    compEndDate || moment(),
    densityRange || DateGroupDays,
    // @ts-ignore
    metricsInfo || ({} as IStreamAggregateCardDataDisplay | IMeasurementAggregateCardDataDisplay),
    !!compareEnabled,
    toggleLiveData,
  );
  let noData = loadingState1 === LoadingStateNoData;
  if (compareEnabled && toggleLiveData) {
    noData = loadingState1 === LoadingStateNoData && loadingState2 === LoadingStateNoData;
  }

  const render = () => {
    return (
      <Paper className={classes.paper} style={{ marginTop: 10, marginBottom: 10 }}>
        <Grid container alignItems='center' spacing={1}>
          <Grid xs={2} item>
            <Typography className={classes.chartName}>{graphTitle}</Typography>
          </Grid>
          <Grid xs={8} item>
            {toggleLiveData && (
              <DashboardDataToggles
                classes={classes}
                toggleLiveData={true}
                handleMetricsChange={handleMetricsChange}
                selectedMetrics={selectedMetricsDataToggle}
                graphDataTypes={selectedMetricsGraphData}
                makeDot={MakeDot}
                user={user}
              />
            )}
          </Grid>
          {!toggleLiveData && (
            <Grid xs={2} item>
              <DashboardDateToggles
                classes={classes}
                densityRange={densityRange || DateGroupDays}
                handleDensityRangeChange={handleDensityRangeChange}
                calStartDate={calStartDate || moment()}
                calEndDate={calEndDate || moment()}
                hourAggRangeDisabled
              />
            </Grid>
          )}
          {loaded ? (
            <ResponsiveContainer width='99%' height={400}>
              <ComposedChart
                data={toggleLiveData ? dataLive : data1AggMerged}
                margin={{ top: 5, right: 10, bottom: 5, left: 25 }}
              >
                {generateGraphs(lineDataKeys)}
                <XAxis dataKey={'Date'} angle={-45} textAnchor='end' height={100} />
                {yAxes}
                <Tooltip
                  wrapperStyle={{ width: 300, zIndex: 999 }}
                  content={
                    <DashboardTooltipV2
                      densityRange={densityRange}
                      loaded={loaded}
                      currencySymbol={currencySymbol || ''}
                    />
                  }
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : noData ? (
            <Grid container alignItems='center' direction='column'>
              <Grid
                item
                style={{
                  width: '99%',
                  height: 400,
                  textAlign: 'center',
                }}
              >
                <Typography style={{ marginTop: 150 }}>
                  No data to display for the selected period or filters
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Grid container alignItems='center' direction='column'>
              <Grid
                item
                style={{
                  width: '99%',
                  height: 400,
                  textAlign: 'center',
                }}
              >
                <Typography style={{ marginTop: 150 }}>Loading Query...</Typography>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };
  return render();
};
